import { Controller } from "@hotwired/stimulus";

// Client-side sort + pagination for a report table card. Sort/page persist to localStorage and re-apply
// on connect(), which re-runs after an ActionCable refresh re-renders the frame — so they survive it.
export default class extends Controller {
  static targets = [
    "mount",
    "template",
    "pageStatus",
    "pager",
    "chevronLeft",
    "chevronRight",
    "ellipsis",
  ];
  static values = {
    storageKey: String,
    pageSize: { type: Number, default: 25 },
    pagerClasses: Object,
  };

  connect() {
    this.allRows = [
      ...this.templateTarget.content.querySelectorAll("tbody tr"),
    ].map((tr) => tr.cloneNode(true));
    // Delegated (not per-button data-action) because the table is cloned in and swapped on every render,
    // so Stimulus wouldn't re-bind actions on the fresh nodes.
    this.onMountClick = (event) => {
      const button = event.target.closest("button[data-column]");
      if (button) this.sort(button.dataset.column);
    };
    this.mountTarget.addEventListener("click", this.onMountClick);
    this.onPagerClick = (event) => {
      const button = event.target.closest("button[data-page]");
      if (button) this.goToPage(Number(button.dataset.page));
    };
    this.pagerTarget.addEventListener("click", this.onPagerClick);

    const state = this.readState();
    this.sortColumn = state.sortColumn ?? null;
    this.sortDirection = state.sortDirection ?? null;
    this.page = state.page ?? 1;
    this.render();
  }

  disconnect() {
    this.mountTarget.removeEventListener("click", this.onMountClick);
    this.pagerTarget.removeEventListener("click", this.onPagerClick);
  }

  goToPage(page) {
    if (page >= 1 && page <= this.pageCount && page !== this.page) {
      this.page = page;
      this.persist();
      this.render();
    }
  }

  sort(column) {
    if (this.sortColumn !== column) {
      this.sortColumn = column;
      this.sortDirection = "asc";
    } else if (this.sortDirection === "asc") {
      this.sortDirection = "desc";
    } else {
      this.sortColumn = null;
      this.sortDirection = null;
    }
    this.page = 1;
    this.persist();
    this.render();
  }

  render() {
    const sorted = this.sortedRows();
    this.pageCount = Math.max(1, Math.ceil(sorted.length / this.pageSizeValue));
    if (this.page > this.pageCount) this.page = this.pageCount;

    const start = (this.page - 1) * this.pageSizeValue;
    const pageRows = sorted.slice(start, start + this.pageSizeValue);

    // Clone the whole wrapper, not just the <table>, to keep the card's scroll area and layout.
    const wrapper =
      this.templateTarget.content.firstElementChild.cloneNode(true);
    const tbody = wrapper.querySelector("tbody");
    tbody.replaceChildren(...pageRows.map((tr) => tr.cloneNode(true)));
    this.markSortedHeader(wrapper);

    // Replacing the subtree reconnects the nested column-order controller against this page's rows.
    this.mountTarget.replaceChildren(wrapper);
    this.updatePager(sorted.length, start, pageRows.length);
  }

  sortedRows() {
    if (!this.sortColumn) return this.allRows;
    const dir = this.sortDirection === "desc" ? -1 : 1;
    // Stable sort; blanks pinned last regardless of direction.
    return this.allRows
      .map((row, index) => ({
        row,
        index,
        text: this.cellText(row, this.sortColumn),
      }))
      .sort((a, b) => {
        if (a.text === "" || b.text === "")
          return (
            (a.text === "" ? 1 : 0) - (b.text === "" ? 1 : 0) ||
            a.index - b.index
          );
        const cmp = this.compare(a.text, b.text);
        return cmp !== 0 ? dir * cmp : a.index - b.index;
      })
      .map((entry) => entry.row);
  }

  compare(a, b) {
    const an = this.asNumber(a);
    const bn = this.asNumber(b);
    if (an !== null && bn !== null) return an - bn;
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  asNumber(text) {
    const cleaned = text.replace(/[$,%\s]/g, "");
    if (cleaned === "" || Number.isNaN(Number(cleaned))) return null;
    return Number(cleaned);
  }

  cellText(row, column) {
    const cell = row.querySelector(
      `[data-table-card-column="${CSS.escape(column)}"]`,
    );
    if (!cell) return "";
    // Prefer the raw value (set server-side) so formatted cells ($1,000, 1h 2m) still sort by their
    // underlying number rather than the display text.
    const raw = cell.dataset.tableCardSortValue;
    return (raw ?? cell.textContent).trim();
  }

  markSortedHeader(table) {
    table.querySelectorAll("button[data-column]").forEach((header) => {
      const active =
        this.sortColumn && header.dataset.column === this.sortColumn;
      const idle = header.querySelector(".sort-idle");
      const asc = header.querySelector(".sort-asc");
      const desc = header.querySelector(".sort-desc");
      if (!idle || !asc || !desc) return;
      idle.classList.toggle("hidden", active);
      asc.classList.toggle("hidden", !(active && this.sortDirection === "asc"));
      desc.classList.toggle(
        "hidden",
        !(active && this.sortDirection === "desc"),
      );
    });
  }

  updatePager(total, start, shown) {
    this.pageStatusTarget.textContent =
      total === 0
        ? "No rows"
        : `${(start + 1).toLocaleString()}–${(start + shown).toLocaleString()} of ${total.toLocaleString()}`;
    this.renderPager();
  }

  // Condensed page list matching shared/_pagination.html.erb (first / ellipsis / neighbors / last).
  renderPager() {
    if (this.pageCount <= 1) {
      this.pagerTarget.replaceChildren();
      return;
    }
    const list = document.createElement("ul");
    list.className = "flex flex-row items-center gap-1";

    list.appendChild(this.navButton("prev", this.page > 1));
    this.pageNumbers().forEach((n) =>
      list.appendChild(n === "ellipsis" ? this.ellipsis() : this.pageButton(n)),
    );
    list.appendChild(this.navButton("next", this.page < this.pageCount));

    const nav = document.createElement("nav");
    nav.setAttribute("role", "navigation");
    nav.setAttribute("aria-label", "pagination");
    nav.className = "flex justify-end";
    nav.appendChild(list);
    this.pagerTarget.replaceChildren(nav);
  }

  pageNumbers() {
    if (this.pageCount <= 7) {
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    }
    const pages = [1];
    if (this.page > 3) pages.push("ellipsis");
    for (
      let n = Math.max(this.page - 1, 2);
      n <= Math.min(this.page + 1, this.pageCount - 1);
      n++
    )
      pages.push(n);
    if (this.page < this.pageCount - 2) pages.push("ellipsis");
    pages.push(this.pageCount);
    return pages;
  }

  pageButton(n) {
    const li = document.createElement("li");
    const active = n === this.page;
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.page = n;
    button.className = active
      ? this.pagerClassesValue.active
      : this.pagerClassesValue.inactive;
    if (active) button.setAttribute("aria-current", "page");
    button.textContent = n.toLocaleString();
    li.appendChild(button);
    return li;
  }

  navButton(direction, enabled) {
    const li = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = enabled
      ? this.pagerClassesValue.nav
      : this.pagerClassesValue.navDisabled;
    button.setAttribute(
      "aria-label",
      direction === "prev" ? "Go to previous page" : "Go to next page",
    );
    if (enabled)
      button.dataset.page =
        direction === "prev" ? this.page - 1 : this.page + 1;
    else button.disabled = true;

    const label = document.createElement("span");
    label.className = "hidden sm:block";
    const chevron = (
      direction === "prev" ? this.chevronLeftTarget : this.chevronRightTarget
    ).content.firstElementChild.cloneNode(true);
    if (direction === "prev") {
      label.textContent = "Previous";
      button.append(chevron, label);
    } else {
      label.textContent = "Next";
      button.append(label, chevron);
    }
    li.appendChild(button);
    return li;
  }

  ellipsis() {
    const li = document.createElement("li");
    const span = document.createElement("span");
    span.setAttribute("aria-hidden", "true");
    span.className = "flex size-9 items-center justify-center";
    span.appendChild(
      this.ellipsisTarget.content.firstElementChild.cloneNode(true),
    );
    li.appendChild(span);
    return li;
  }

  persist() {
    this.writeState({
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      page: this.page,
    });
  }

  get storageKey() {
    return `tableCard.${this.storageKeyValue}`;
  }

  readState() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || {};
    } catch {
      return {};
    }
  }

  writeState(state) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      // localStorage unavailable (private mode / quota) — sorting/paging still work for this render.
    }
  }
}
